// FT-097 (REQ-03): предзаполнение последнего шага визарда регистрации
// данными браузера — таймзона + язык → страна, чтобы пользователю оставалось
// только подтвердить поля.
//
// Источник таймзоны — Intl.DateTimeFormat().resolvedOptions().timeZone
// (настройка ОС), а не IP-геолокация: осознанный выбор, устойчивый к VPN.
// Если зона неизвестна нашей таблице (или сам Intl недоступен в рантайме),
// вторая ступень — язык браузера (navigator.language).
//
// Инвариант обеих ступеней: возвращаемое имя страны ОБЯЗАНО существовать в
// фактическом справочнике опций селекта src/components/countries.js (поле
// `name` — это же item-value автокомплита страны в Signup.vue), иначе
// Vuetify-селект не подхватит предзаполненное значение. Значения таблиц ниже
// сверены напрямую со countries.js (см. tests/unit/helpers/geoPrefill.spec.js
// и отчёт задачи D.1).
//
// Таблицы покрывают страны реального присутствия: СНГ + ЕС/другие рынки,
// совпадающие с фактическим набором локалей src/locales. Для
// незнакомой зоны/языка — null, без угадывания.

// IANA-таймзона → страна (name из countries.js). Для стран с несколькими
// зонами (Россия, Казахстан) перечислены все актуальные IANA-имена, чтобы
// пользователь из любого региона страны получил верное предзаполнение.
//
// Экспортируется (а не только через countryFromTimezone) специально для
// тест-сюита: спека обязана итерировать по ВСЕМ фактическим записям этой
// таблицы и сверять КАЖДОЕ значение со справочником countries.js, а не по
// хардкоженной выборке примеров — иначе будущая правка countries.js (или
// опечатка здесь) молча разойдётся с UI и тест этого не заметит.
export const TZ_TO_COUNTRY = {
  // Russia — все актуальные зоны РФ (запад → восток)
  "Europe/Kaliningrad": "Russia",
  "Europe/Moscow": "Russia",
  "Europe/Samara": "Russia",
  "Europe/Volgograd": "Russia",
  "Asia/Yekaterinburg": "Russia",
  "Asia/Omsk": "Russia",
  "Asia/Novosibirsk": "Russia",
  "Asia/Barnaul": "Russia",
  "Asia/Tomsk": "Russia",
  "Asia/Novokuznetsk": "Russia",
  "Asia/Krasnoyarsk": "Russia",
  "Asia/Irkutsk": "Russia",
  "Asia/Chita": "Russia",
  "Asia/Yakutsk": "Russia",
  "Asia/Khandyga": "Russia",
  "Asia/Vladivostok": "Russia",
  "Asia/Ust-Nera": "Russia",
  "Asia/Magadan": "Russia",
  "Asia/Sakhalin": "Russia",
  "Asia/Srednekolymsk": "Russia",
  "Asia/Kamchatka": "Russia",
  "Asia/Anadyr": "Russia",

  // Belarus
  "Europe/Minsk": "Belarus",

  // Kazakhstan — обе зоны (реформа 2024 объединила часы, но IANA-имена живы)
  "Asia/Almaty": "Kazakhstan",
  "Asia/Qyzylorda": "Kazakhstan",
  "Asia/Aqtobe": "Kazakhstan",
  "Asia/Aqtau": "Kazakhstan",
  "Asia/Atyrau": "Kazakhstan",
  "Asia/Oral": "Kazakhstan",

  // остальные страны СНГ присутствия
  "Asia/Yerevan": "Armenia",
  "Asia/Baku": "Azerbaijan",
  "Asia/Tbilisi": "Georgia",
  "Asia/Bishkek": "Kyrgyzstan",
  "Europe/Chisinau": "Moldova",
  "Asia/Dushanbe": "Tajikistan",
  "Asia/Ashgabat": "Turkmenistan",
  "Europe/Kiev": "Ukraine",
  "Europe/Kyiv": "Ukraine",
  "Europe/Uzhgorod": "Ukraine",
  "Europe/Zaporozhye": "Ukraine",
  "Asia/Tashkent": "Uzbekistan",
  "Asia/Samarkand": "Uzbekistan",

  // ЕС и другие рынки присутствия (совпадают с набором локалей src/locales)
  "Europe/Berlin": "Germany",
  "Europe/Busingen": "Germany",
  "Europe/Madrid": "Spain",
  "Atlantic/Canary": "Spain",
  "Africa/Ceuta": "Spain",
  "Europe/Paris": "France",
  "Europe/Rome": "Italy",
  "Europe/Warsaw": "Poland",
  "Europe/Bucharest": "Romania",
  "Europe/Lisbon": "Portugal",
  "Atlantic/Madeira": "Portugal",
  "Atlantic/Azores": "Portugal",
  "Europe/Belgrade": "Serbia",
  "Europe/Zagreb": "Croatia",
  "Europe/Athens": "Greece",
  "Europe/Istanbul": "Turkey",
  "Asia/Bangkok": "Thailand",
  "Asia/Jakarta": "Indonesia",
  "Asia/Pontianak": "Indonesia",
  "Asia/Makassar": "Indonesia",
  "Asia/Jayapura": "Indonesia",
};

// Язык (primary subtag BCP-47) → страна (name из countries.js). Вторая
// ступень REQ-03 — фолбэк, когда таймзона не распозналась таблицей выше.
// Экспортируется по той же причине, что и TZ_TO_COUNTRY выше — полное
// покрытие тестом, а не выборка.
export const LOCALE_TO_COUNTRY = {
  ru: "Russia",
  be: "Belarus",
  kk: "Kazakhstan",
  hy: "Armenia",
  az: "Azerbaijan",
  ka: "Georgia",
  ky: "Kyrgyzstan",
  tg: "Tajikistan",
  tk: "Turkmenistan",
  uk: "Ukraine",
  uz: "Uzbekistan",
  de: "Germany",
  es: "Spain",
  fr: "France",
  it: "Italy",
  pl: "Poland",
  ro: "Romania",
  pt: "Portugal",
  sr: "Serbia",
  hr: "Croatia",
  el: "Greece",
  tr: "Turkey",
  th: "Thailand",
  id: "Indonesia",
};

// Фактический набор локалей проекта (src/locales/*.json) — значение
// по умолчанию для preferredLocale.
export const AVAILABLE_LOCALES = [
  "de",
  "el",
  "en",
  "es",
  "fr",
  "hr",
  "id",
  "it",
  "pl",
  "pt",
  "ro",
  "ru",
  "sr",
  "th",
  "tr",
];

// Primary subtag BCP-47 ("ru-RU" → "ru"), lowercased. null для пустого/не-строки.
function primarySubtag(tag) {
  if (!tag || typeof tag !== "string") return null;
  const primary = tag.split("-")[0].trim().toLowerCase();
  return primary || null;
}

// Таймзона окружения ОС — устойчива к VPN (в отличие от IP-геолокации, это
// осознанный выбор источника). null, если Intl недоступен в рантайме
// (старый WebView) или бросил исключение.
export function detectTimezone() {
  try {
    if (typeof Intl === "undefined" || !Intl.DateTimeFormat) return null;
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

// tz (IANA) → имя страны из countries.js (`name`), либо null для неизвестной/
// непокрытой зоны — без угадывания.
export function countryFromTimezone(tz) {
  if (!tz) return null;
  return TZ_TO_COUNTRY[tz] || null;
}

// lang (BCP-47, например navigator.language) → имя страны из countries.js
// (`name`), либо null. Вторая ступень REQ-03.
export function countryFromLocale(lang) {
  const primary = primarySubtag(lang);
  if (!primary) return null;
  return LOCALE_TO_COUNTRY[primary] || null;
}

// Пересечение navigator.language с доступными локалями проекта (∩); при
// пустом пересечении/недоступном navigator — "en".
export function preferredLocale(available = AVAILABLE_LOCALES) {
  const lang = typeof navigator !== "undefined" ? navigator.language : null;
  const primary = primarySubtag(lang);
  if (primary && Array.isArray(available) && available.includes(primary)) {
    return primary;
  }
  return "en";
}
